import { useEffect, useState } from "react";
import { OnboardingStep } from "../onboarding-components/onboarding-step";
import { CountryEnum } from "@/lib/supabase/supabaseEnums";
import { validateGLN } from "../onboarding-validations";
import { CustomTextFieldShadcn } from "@/dentlab/src/forms/form-input-components/custom-input-components/custom-text-field-shadcn.component";
import { CountrySelectShadcn } from "@/dentlab/src/forms/form-input-components/selects/country-select/country.select";
import { CitySelectShadcn } from "@/dentlab/src/forms/form-input-components/selects/city-select/city-select-shadcn";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { useAttributesStore } from "@/dentlab/src/store/Attributes/attributes.store";
import { ONBOARDING_STEPS, ONBOARDING_STATUS } from "@/dentlab/src/constants/onboarding";
import { environment } from "@/dentlab/src/lib/utils/environment";
import { hasSwissPostalCodes } from "@/dentlab/src/lib/utils/country-utils";
import { Logger } from "@/lib/logger/Logger";
import { OrganizationMockup } from "../onboarding-components/onboarding-mockups/OrganizationMockup";
import { StepProps } from "./types";

interface OrganizationValidationParams {
    organizationName: string;
    country: CountryEnum | null;
    city: string;
    streetAndNumber: string;
    postalCode: string;
    gln: string;
}

const validateOrganization = (params: OrganizationValidationParams): string | null => {
    if (!params.organizationName.trim()) return "Bitte geben Sie den Namen der Organisation ein";
    if (!params.country) return "Bitte wählen Sie ein Land aus";
    if (!params.city.trim()) return "Bitte geben Sie eine Stadt ein";
    if (!params.streetAndNumber.trim()) return "Bitte geben Sie eine Strasse ein";
    if (!params.postalCode.trim()) return "Bitte geben Sie eine Postleitzahl ein";
    if (!validateGLN(params.gln)) return "Ungültige GLN-Nummer (13 Ziffern erforderlich)";
    return null;
};

interface StepOrganizationInformationProps extends StepProps { }

export const StepOrganizationInformation: React.FC<StepOrganizationInformationProps> = ({ onComplete, stepNumber, totalSteps, canGoBack, onGoBack }) => {
    const { organization, profile, updateOrganizationBatch, getOrganization } = useCentralStore((state) => ({
        organization: state.organization,
        profile: state.profile,
        updateOrganizationBatch: state.updateOrganizationBatch,
        getOrganization: state.getOrganization,
    }));
    const setOnboardingAttribute = useAttributesStore((state) => state.setOnboardingAttribute);

    const [organizationName, setOrganizationName] = useState("");
    const [country, setCountry] = useState<CountryEnum>(CountryEnum.SWITZERLAND);
    const [city, setCity] = useState("");
    const [streetAndNumber, setStreetAndNumber] = useState("");
    const [postalCode, setPostalCode] = useState("");
    const [gln, setGln] = useState("");
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        if (organization) {
            if (organization.name) setOrganizationName(organization.name);
            if (organization.country) setCountry(organization.country as CountryEnum);
            if (organization.city) setCity(organization.city);
            if (organization.street) setStreetAndNumber(organization.street);
            if (organization.postal_code) setPostalCode(organization.postal_code);
            if (organization.gln) setGln(organization.gln);
        }
    }, []);

    const handleSubmit = async () => {
        const error = validateOrganization({ organizationName, country, city, streetAndNumber, postalCode, gln });
        if (error) {
            setErrorMessage(error);
            return;
        }

        if (!organization?.id || !profile?.email) {
            setErrorMessage("Organisationsdaten nicht gefunden");
            return;
        }

        setLoading(true);
        setErrorMessage(null);

        try {
            const { error: updateError } = await updateOrganizationBatch({
                name: organizationName,
                street: streetAndNumber,
                postal_code: postalCode,
                city: city,
                country: country,
                gln: gln || null,
            }, true);

            if (updateError) {
                Logger.error(updateError, {}, "Error updating organization");
                setErrorMessage("Fehler beim Speichern der Organisationsdaten");
                setLoading(false);
                return;
            }

            // 2. Create Stripe customer
            const response = await fetch(
                `${environment.VITE_PDF_MICROSERVICE_URL}/stripe/create-customer`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        email: profile.email,
                        organization_name: organizationName,
                        street_and_number: streetAndNumber,
                        postal_code: postalCode,
                        city: city,
                        country: country,
                        organization_id: organization.id,
                    }),
                }
            );

            if (!response.ok) {
                Logger.error("Failed to create Stripe customer: " + response.status);
                setErrorMessage("Fehler beim Erstellen des Kundenprofils");
                setLoading(false);
                return;
            }

            // Since calling the stripe endpoint effects the trial state, refetch the organization
            await getOrganization();

            await setOnboardingAttribute(ONBOARDING_STEPS.ORGANIZATION_INFO, ONBOARDING_STATUS.COMPLETED, 'org');
            onComplete();
        } catch (error) {
            Logger.error(error, {}, "Error in step 2 submit");
            setErrorMessage("Ein Fehler ist aufgetreten");
        } finally {
            setLoading(false);
        }
    }

    return (
        <OnboardingStep
            stepTitle="Ihr Labor"
            stepDescription="Diese Angaben erscheinen auf Rechnungen, Lieferscheinen und anderen Dokumenten."
            stepNumber={stepNumber}
            totalSteps={totalSteps}
            onButtonClick={handleSubmit}
            canGoBack={canGoBack}
            onGoBackClick={onGoBack}
            buttonText="Weiter"
            buttonDisabled={false}
            loading={loading}
            errorMessage={errorMessage}
            onErrorMessageClose={() => setErrorMessage(null)}
            rightPanel={
                <OrganizationMockup
                    name={organizationName}
                    street={streetAndNumber}
                    postalCode={postalCode}
                    city={city}
                />
            }
        >
            <div className="space-y-2 sm:space-y-3">
                {/* Row 1: Organization Name (full width) */}
                <CustomTextFieldShadcn
                    label="Name der Organisation"
                    id="organizationName"
                    name="organizationName"
                    autoComplete="organization"
                    required
                    value={organizationName}
                    onChange={(e) => setOrganizationName(e.target.value)}
                    classNameInput="h-9 sm:h-10"
                    placeholder="Dentallabor GmbH"
                    data-testid="signup-organization-input"
                />
                <CountrySelectShadcn
                    onChange={(value) =>
                        setCountry(value as CountryEnum)
                    }
                    value={country}
                    triggerClassName="w-full h-9 sm:h-10"
                    required
                />
                <CustomTextFieldShadcn
                    label="Strasse und Hausnummer"
                    id="streetAndNumber"
                    name="streetAndNumber"
                    value={streetAndNumber}
                    onChange={(e) => setStreetAndNumber(e.target.value)}
                    required
                    classNameInput="h-9 sm:h-10"
                    placeholder="Musterstrasse 123"
                    data-testid="signup-street-input"
                />
                {hasSwissPostalCodes(country) ? (
                    <CitySelectShadcn
                        onChange={(value) => {
                            setCity(value.city)
                            setPostalCode(value.postalCode)
                        }}
                        value={
                            postalCode && city
                                ? postalCode + " " + city
                                : ""
                        }
                        required={true}
                        triggerClassName="h-9 sm:h-10"
                    />
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 items-start">
                        <CustomTextFieldShadcn
                            label="PLZ"
                            id="postalCode"
                            name="postalCode"
                            value={postalCode}
                            onChange={(e) => setPostalCode(e.target.value)}
                            required
                            classNameInput="h-9 sm:h-10"
                            data-testid="signup-postalcode-input"
                        />
                        <CustomTextFieldShadcn
                            label="Ort"
                            id="city"
                            name="city"
                            value={city}
                            onChange={(e) => setCity(e.target.value)}
                            required
                            classNameInput="h-9 sm:h-10"
                            data-testid="signup-city-input"
                        />
                    </div>
                )}
                <CustomTextFieldShadcn
                    label="GLN"
                    id="gln"
                    name="gln"
                    type="text"
                    value={gln}
                    onChange={(e) => setGln(e.target.value)}
                    classNameInput="h-9 sm:h-10"
                    customHelperText="GLN (Global Location Number) - 13-stellige Nummer zur Identifikation im Gesundheitswesen"
                    data-testid="signup-gln-input"
                />
            </div>
        </OnboardingStep>
    )
}